"use client";
import React from "react";
import { AbsoluteFill, Audio, Img, Sequence, interpolate, spring, staticFile, useCurrentFrame, useVideoConfig, Easing } from "remotion";

// ─────────────────────────────────────────────────────────────────────────────
// FACTORY PROCESS DECODER — RUNNER CTA (Yash, 20 Oct 2026)
// A character runs in along the bottom of the frame, pulls out a Like · Share · Subscribe
// strip, taps each (click sounds; bell on Subscribe), runs off to the right, comes back a
// little, drags the strip off to the right and everything fades. Three characters:
//   "worker" (hard-hat factory worker) · "robot" · "cog" (gear mascot).
// Designed at 1920×1080 and scaled to the composition width, so the same component renders
// natively at 3840×2160 for the 4K master (composite AFTER the NVIDIA VSR upscale).
// 24 fps, 8 s (192 frames). Use: <Sequence from={…} durationInFrames={192}><FpdCtaRunner character="cog"/></Sequence>
// ─────────────────────────────────────────────────────────────────────────────

export type CtaCharacter = "worker" | "robot" | "cog";
export const FPD_CTA_FRAMES = 192;

// beat grid (frames @24 fps)
const B = { inEnd: 24, pull: 26, like: 50, share: 68, sub: 88, awayStart: 112, awayEnd: 134, backStart: 140, backEnd: 154, dragStart: 158, dragEnd: 186 };
const GROUND = 1018;           // feet line (1080p design space)
const STOP_X = 520;            // where the character stands to pull the strip out
const STRIP_X = 600, STRIP_W = 760, STRIP_Y = 905, STRIP_H = 86;

const ease = Easing.bezier(0.25, 0.1, 0.25, 1);
function charX(f: number): number {
  if (f <= B.inEnd) return interpolate(f, [0, B.inEnd], [-240, STOP_X], { easing: Easing.out(Easing.cubic), extrapolateRight: "clamp" });
  if (f < B.awayStart) return STOP_X;
  if (f <= B.awayEnd) return interpolate(f, [B.awayStart, B.awayEnd], [STOP_X, 2200], { easing: Easing.in(Easing.quad) });
  if (f < B.backStart) return 2200;
  if (f <= B.backEnd) return interpolate(f, [B.backStart, B.backEnd], [2200, STRIP_X + STRIP_W + 70], { easing: Easing.out(Easing.cubic) });
  if (f < B.dragStart) return STRIP_X + STRIP_W + 70;
  return interpolate(f, [B.dragStart, B.dragEnd], [STRIP_X + STRIP_W + 70, STRIP_X + STRIP_W + 70 + 620], { easing: ease, extrapolateRight: "clamp" });
}
const running = (f: number) => (f < B.inEnd) || (f >= B.awayStart && f <= B.awayEnd) || (f >= B.backStart && f <= B.backEnd) || (f >= B.dragStart && f <= B.dragEnd);
const facingLeft = (f: number) => f >= B.backStart;          // comes back from the right
const tapAt = [B.like, B.share, B.sub];

// ── characters (drawn in a 200×220 box, feet at y=220) ──────────────────────
type Pose = { leg: number; arm: number; bob: number; reach: number; blink: boolean };
function pose(f: number): Pose {
  const run = running(f);
  const ph = f * 0.95;
  const tap = tapAt.find(t => f >= t - 6 && f <= t + 6);
  const reach = tap !== undefined ? interpolate(Math.abs(f - tap), [0, 6], [1, 0]) : (f >= B.pull - 2 && f <= B.pull + 12 ? interpolate(f, [B.pull - 2, B.pull + 6, B.pull + 12], [0, 1, 0]) : (f >= B.backEnd && f <= B.dragEnd ? 1 : 0));
  return { leg: run ? 34 * Math.sin(ph) : 0, arm: run ? -30 * Math.sin(ph) : 4 * Math.sin(f * 0.15), bob: run ? Math.abs(Math.sin(ph)) * 7 : (tap !== undefined ? -interpolate(Math.abs(f - tap), [0, 6], [14, 0]) : 0), reach, blink: f % 70 > 66 };
}
const Limb: React.FC<{ x: number; y: number; len: number; angle: number; w: number; color: string; foot?: string }> = ({ x, y, len, angle, w, color, foot }) => (
  <g transform={`translate(${x} ${y}) rotate(${angle})`}>
    <rect x={-w / 2} y={0} width={w} height={len} rx={w / 2} fill={color} />
    {foot && <rect x={-w / 2 - 2} y={len - 8} width={w + 14} height={12} rx={5} fill={foot} />}
  </g>
);

const Worker: React.FC<{ p: Pose }> = ({ p }) => (
  <g>
    <Limb x={84} y={150} len={64} angle={p.leg} w={20} color="#1f3b73" foot="#3b2a1a" />
    <Limb x={116} y={150} len={64} angle={-p.leg} w={20} color="#24467f" foot="#3b2a1a" />
    <Limb x={66} y={88} len={58} angle={20 - p.arm} w={16} color="#e9b48a" />
    <rect x={62} y={78} width={76} height={82} rx={16} fill="#2c5aa0" />
    <rect x={62} y={78} width={76} height={82} rx={16} fill="none" stroke="#14305f" strokeWidth={3} />
    <rect x={68} y={84} width={14} height={70} fill="#ff8a1e" /><rect x={118} y={84} width={14} height={70} fill="#ff8a1e" />
    <rect x={68} y={118} width={64} height={8} fill="#e8ff6a" opacity={0.9} />
    <g transform={`translate(134 90) rotate(${-20 + p.arm - p.reach * 95})`}>
      <rect x={-8} y={0} width={16} height={58} rx={8} fill="#e9b48a" /><circle cx={0} cy={60} r={10} fill="#ffd23f" />
    </g>
    <circle cx={100} cy={52} r={30} fill="#f0c09a" />
    {p.blink ? <rect x={86} y={52} width={10} height={3} fill="#222" /> : <circle cx={91} cy={52} r={4} fill="#222" />}
    {p.blink ? <rect x={106} y={52} width={10} height={3} fill="#222" /> : <circle cx={111} cy={52} r={4} fill="#222" />}
    <path d="M90 64 Q101 72 112 64" stroke="#7a3b2a" strokeWidth={3} fill="none" strokeLinecap="round" />
    <path d="M66 42 Q100 2 134 42 Z" fill="#ffc400" stroke="#c99400" strokeWidth={3} /><rect x={60} y={38} width={80} height={10} rx={5} fill="#ffc400" stroke="#c99400" strokeWidth={2} />
  </g>
);

const Robot: React.FC<{ p: Pose; f: number }> = ({ p, f }) => (
  <g>
    <Limb x={84} y={152} len={62} angle={p.leg} w={16} color="#8d99a6" foot="#3d4752" />
    <Limb x={116} y={152} len={62} angle={-p.leg} w={16} color="#a3aeba" foot="#3d4752" />
    <Limb x={62} y={96} len={54} angle={20 - p.arm} w={13} color="#9aa6b2" />
    <rect x={58} y={84} width={84} height={76} rx={10} fill="#c9d2db" stroke="#5b6773" strokeWidth={3} />
    <circle cx={100} cy={120} r={13} fill={f % 24 < 12 ? "#22e3ff" : "#0aa5c0"} stroke="#0d5f6e" strokeWidth={3} />
    <g transform={`translate(138 96) rotate(${-20 + p.arm - p.reach * 95})`}>
      <rect x={-6.5} y={0} width={13} height={54} rx={6} fill="#9aa6b2" /><path d="M-10 54 L0 66 L10 54" stroke="#3d4752" strokeWidth={5} fill="none" />
    </g>
    <rect x={64} y={24} width={72} height={56} rx={12} fill="#dfe6ec" stroke="#5b6773" strokeWidth={3} />
    <rect x={74} y={38} width={52} height={22} rx={8} fill="#18202a" />
    {!p.blink && <><circle cx={88} cy={49} r={6} fill="#22e3ff" /><circle cx={112} cy={49} r={6} fill="#22e3ff" /></>}
    <line x1={100} y1={24} x2={100} y2={8} stroke="#5b6773" strokeWidth={4} /><circle cx={100} cy={6} r={6} fill={f % 16 < 8 ? "#ff3b3b" : "#7a1010"} />
  </g>
);

const Cog: React.FC<{ p: Pose; f: number }> = ({ p, f }) => {
  const teeth = Array.from({ length: 10 }, (_, i) => i * 36);
  const spin = running(f) ? f * 9 : 0;
  return (
    <g>
      <Limb x={86} y={160} len={56} angle={p.leg} w={14} color="#2b2b2b" foot="#111" />
      <Limb x={114} y={160} len={56} angle={-p.leg} w={14} color="#2b2b2b" foot="#111" />
      <Limb x={52} y={110} len={50} angle={30 - p.arm} w={12} color="#2b2b2b" />
      <g transform={`translate(100 108) rotate(${spin})`}>
        {teeth.map(a => <rect key={a} x={-11} y={-72} width={22} height={24} rx={4} fill="#f08a1c" stroke="#a85300" strokeWidth={2} transform={`rotate(${a})`} />)}
        <circle r={56} fill="#f08a1c" stroke="#a85300" strokeWidth={4} />
        <circle r={14} fill="#a85300" opacity={0.35} />
      </g>
      <g transform={`translate(150 104) rotate(${-30 + p.arm - p.reach * 95})`}>
        <rect x={-6} y={0} width={12} height={52} rx={6} fill="#2b2b2b" /><circle cx={0} cy={56} r={9} fill="#fff" stroke="#2b2b2b" strokeWidth={3} />
      </g>
      {p.blink ? <><rect x={78} y={96} width={14} height={4} fill="#1b1b1b" /><rect x={108} y={96} width={14} height={4} fill="#1b1b1b" /></>
        : <><ellipse cx={85} cy={96} rx={9} ry={11} fill="#fff" /><ellipse cx={115} cy={96} rx={9} ry={11} fill="#fff" /><circle cx={87} cy={98} r={4.5} fill="#1b1b1b" /><circle cx={117} cy={98} r={4.5} fill="#1b1b1b" /></>}
      <path d="M86 120 Q100 132 114 120" stroke="#1b1b1b" strokeWidth={4} fill="none" strokeLinecap="round" />
      <rect x={70} y={62} width={60} height={8} rx={4} fill="#1b1b1b" opacity={0.85} />
    </g>
  );
};

// ── the button strip ────────────────────────────────────────────────────────
const Thumb: React.FC<{ on: boolean }> = ({ on }) => (
  <svg width={34} height={34} viewBox="0 0 24 24"><path d="M2 21h4V9H2v12zm20-11c0-1.1-.9-2-2-2h-6.3l1-4.6v-.3c0-.4-.2-.8-.4-1.1L13.2 1 6.6 7.6C6.2 8 6 8.5 6 9v10c0 1.1.9 2 2 2h9c.8 0 1.5-.5 1.8-1.2l3-7.1c.1-.2.2-.5.2-.7v-2z" fill={on ? "#3ea6ff" : "none"} stroke={on ? "#3ea6ff" : "#fff"} strokeWidth={on ? 0 : 1.6} /></svg>
);
const ShareIco: React.FC<{ on: boolean }> = ({ on }) => (
  <svg width={34} height={34} viewBox="0 0 24 24"><path d="M14 4l8 8-8 8v-5c-6 0-9 2-12 6 1-7 5-11 12-12V4z" fill={on ? "#3ea6ff" : "none"} stroke={on ? "#3ea6ff" : "#fff"} strokeWidth={1.6} strokeLinejoin="round" /></svg>
);
const Bell: React.FC<{ rot: number; on: boolean }> = ({ rot, on }) => (
  <svg width={34} height={34} viewBox="0 0 24 24" style={{ transform: `rotate(${rot}deg)`, transformOrigin: "50% 10%" }}>
    <path d="M12 22a2.5 2.5 0 0 0 2.5-2.5h-5A2.5 2.5 0 0 0 12 22zm7-6V11c0-3.3-2.1-6-5-6.8V3.5a2 2 0 1 0-4 0v.7C7.1 5 5 7.7 5 11v5l-2 2v1h18v-1l-2-2z" fill={on ? "#fff" : "none"} stroke="#fff" strokeWidth={1.4} />
  </svg>
);

const Pill: React.FC<{ children: React.ReactNode; bg: string; press: number; w: number }> = ({ children, bg, press, w }) => (
  <div style={{ width: w, height: 62, borderRadius: 31, background: bg, display: "flex", alignItems: "center", justifyContent: "center", gap: 12,
    transform: `scale(${1 - 0.12 * press})`, boxShadow: `0 0 0 ${3 + 6 * press}px rgba(255,255,255,${0.12 + 0.35 * press})`, color: "#fff",
    fontFamily: "Inter, 'Helvetica Neue', Arial, sans-serif", fontWeight: 800, fontSize: 26, letterSpacing: 1 }}>{children}</div>
);

export const FpdCtaRunner: React.FC<{ character: CtaCharacter; withBg?: boolean }> = ({ character, withBg }) => {
  const f = useCurrentFrame();
  const { fps, width } = useVideoConfig();
  const k = width / 1920;
  const p = pose(f);
  const press = (t: number) => (f >= t && f <= t + 8 ? interpolate(f, [t, t + 3, t + 8], [0, 1, 0]) : 0);
  const unroll = spring({ frame: f - B.pull, fps, config: { damping: 14, stiffness: 140 } });
  const dragDx = f < B.dragStart ? 0 : interpolate(f, [B.dragStart, B.dragEnd], [0, 620], { easing: ease, extrapolateRight: "clamp" });
  const fade = interpolate(f, [B.dragStart + 8, B.dragEnd + 2], [1, 0], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const subbed = f >= B.sub + 2;
  const bellRot = f >= B.sub && f < B.sub + 30 ? 22 * Math.sin((f - B.sub) * 1.3) * Math.exp(-(f - B.sub) / 14) : 0;
  const x = charX(f), left = facingLeft(f);
  const Char = character === "worker" ? <Worker p={p} /> : character === "robot" ? <Robot p={p} f={f} /> : <Cog p={p} f={f} />;
  return (
    <AbsoluteFill>
      {withBg && <Img src={staticFile("fpd_cta/bg.jpg")} style={{ width: "100%", height: "100%", objectFit: "cover" }} />}
      <AbsoluteFill style={{ transform: `scale(${k})`, transformOrigin: "0 0", width: 1920, height: 1080 }}>
        {/* the strip */}
        {f >= B.pull && (
          <div style={{ position: "absolute", left: STRIP_X + dragDx, top: STRIP_Y, width: STRIP_W, height: STRIP_H, borderRadius: 43,
            background: "rgba(14,16,20,0.86)", border: "2px solid rgba(255,255,255,0.25)", display: "flex", alignItems: "center", justifyContent: "space-evenly",
            transform: `scaleX(${unroll})`, transformOrigin: "0 50%", opacity: fade, boxShadow: "0 10px 30px rgba(0,0,0,0.45)" }}>
            <Pill bg={f >= B.like + 2 ? "rgba(62,166,255,0.22)" : "rgba(255,255,255,0.12)"} press={press(B.like)} w={170}><Thumb on={f >= B.like + 2} />LIKE</Pill>
            <Pill bg={f >= B.share + 2 ? "rgba(62,166,255,0.22)" : "rgba(255,255,255,0.12)"} press={press(B.share)} w={180}><ShareIco on={f >= B.share + 2} />SHARE</Pill>
            <Pill bg={subbed ? "#3a3a3a" : "#ff0033"} press={press(B.sub)} w={330}>
              <Img src={staticFile("fpd_cta/avatar.png")} style={{ width: 44, height: 44, borderRadius: 22, objectFit: "cover", border: "2px solid #fff" }} />
              {subbed ? "SUBSCRIBED" : "SUBSCRIBE"}<Bell rot={bellRot} on={subbed} />
            </Pill>
          </div>
        )}
        {/* the character */}
        <svg width={200} height={230} viewBox="0 -10 200 230" style={{ position: "absolute", left: x - 100, top: GROUND - 220 + p.bob, opacity: f >= B.dragStart ? fade : 1,
          transform: left ? "scaleX(-1)" : undefined, overflow: "visible", filter: "drop-shadow(0 8px 10px rgba(0,0,0,0.45))" }}>{Char}</svg>
      </AbsoluteFill>
      {/* sound */}
      <Sequence from={0} durationInFrames={B.inEnd}><Audio src={staticFile("fpd_cta/steps.wav")} volume={0.5} /></Sequence>
      <Sequence from={B.pull}><Audio src={staticFile("fpd_cta/pop.wav")} volume={0.7} /></Sequence>
      <Sequence from={B.like}><Audio src={staticFile("fpd_cta/click.wav")} volume={0.9} /></Sequence>
      <Sequence from={B.share}><Audio src={staticFile("fpd_cta/click.wav")} volume={0.9} /></Sequence>
      <Sequence from={B.sub}><Audio src={staticFile("fpd_cta/click.wav")} volume={0.9} /></Sequence>
      <Sequence from={B.sub + 2}><Audio src={staticFile("fpd_cta/bell.wav")} volume={0.8} /></Sequence>
      <Sequence from={B.awayStart} durationInFrames={B.awayEnd - B.awayStart}><Audio src={staticFile("fpd_cta/steps.wav")} volume={0.45} /></Sequence>
      <Sequence from={B.backStart}><Audio src={staticFile("fpd_cta/steps.wav")} volume={0.35} /></Sequence>
      <Sequence from={B.dragStart}><Audio src={staticFile("fpd_cta/whoosh.wav")} volume={0.6} /></Sequence>
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
